import React, { useEffect, useRef, useState } from 'react';
import { EXPLORER_API_BASE_URL } from '../config/api';
import ChibiAvatar, { deriveChibiMood } from './ChibiAvatar';

// Claude the Explorer's Tamagotchi form — a retro virtual-pet shell on
// /explore, alive 24/7 regardless of whether a live session is running.
//
// State source: GET /explorer/pet (polled every 10s — do NOT poll faster,
// the account Lambda concurrency cap is 10). While the bot is LIVE we also
// poll /explorer/thoughts (5s, same as ClaudeExplorerRailCard) so the pet's
// face tracks the real activity feed; offline the face comes from the
// backend's persisted mood_label via ChibiAvatar's moodOverride.
//
// Care buttons (FEED / PET / PLAY) only work offline — while live the bot
// "feeds himself on adventure" and the buttons lock. While asleep only
// quiet petting works. All rules are server-enforced; the client-side
// disables are just good manners.
//
// Additive-only guarantee: renders nothing until the first successful pet
// fetch, so a dead endpoint leaves /explore looking exactly like today.

const EXPLORER_ACCENT = '#bf94ff';
const LCD_BG = '#1a1626';

const PET_POLL_MS = 10 * 1000;
const THOUGHTS_POLL_MS = 5 * 1000;
// Mirrors the server's per-IP-per-action cooldown so we don't invite 429s.
const CARE_COOLDOWN_MS = 30 * 1000;
// How long a care/rejection message owns the marquee before idle copy returns.
const MARQUEE_FLASH_MS = 8 * 1000;

const CARE_BUTTONS = [
  { action: 'feed', label: 'FEED', emoji: '🍖' },
  { action: 'pet', label: 'PET', emoji: '🫳' },
  { action: 'play', label: 'PLAY', emoji: '🎾' },
];

// Idle marquee copy — picked from the pet's neediest stat so the line reads
// like the pet asking for the right thing. Keep lines ≲22 chars: the LCD is
// ~150px wide and longer copy ellipsizes (full text is in the title attr).
function idleMarquee(pet) {
  if (!pet) return '';
  if (pet.is_live) return 'Out exploring! 🦀';
  if (pet.asleep) return 'Zzz… quiet pets only 💤';
  const { hunger, energy, mood } = pet.stats || {};
  if (hunger < 30) return 'Tummy rumbling… 🍖';
  if (energy < 30) return 'Feeling sleepy… 🥱';
  if (mood < 40) return 'Cheer me up? 💜';
  if (hunger < 60) return 'Snack time? 👀';
  return 'Happy you’re here! ✨';
}

function heartsFor(value) {
  return Math.max(0, Math.min(5, Math.round((value || 0) / 20)));
}

function ExplorerTamagotchi() {
  const [pet, setPet] = useState(null);
  // Transient marquee override (care results, rejections). null → idle copy.
  const [flashMsg, setFlashMsg] = useState(null);
  const flashTimer = useRef(null);
  // Per-action client-side cooldowns: { feed: untilTs, ... }
  const [cooldowns, setCooldowns] = useState({});
  // One-shot sparkle burst over the LCD after a successful care action.
  const [sparkle, setSparkle] = useState(null);
  // Live-mode activity feed (drives the face while live).
  const [liveActivity, setLiveActivity] = useState(null);
  const [liveSecondsSince, setLiveSecondsSince] = useState(null);
  // 1s ticker while live so the session progress bar actually moves.
  const [nowTick, setNowTick] = useState(() => Date.now());

  // ── pet poll ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchPet = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/pet`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && json?.ok) setPet(json);
      } catch (err) {
        // Swallow — widget simply doesn't render (or keeps last state).
      }
    };
    fetchPet();
    const id = setInterval(fetchPet, PET_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── live activity poll (only while live) ────────────────────────────────
  const isLive = !!pet?.is_live;
  useEffect(() => {
    if (!isLive) {
      setLiveActivity(null);
      setLiveSecondsSince(null);
      return undefined;
    }
    let cancelled = false;
    const fetchThoughts = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/thoughts`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setLiveActivity(data?.activity || null);
        setLiveSecondsSince(
          typeof data?.seconds_since_last_action === 'number'
            ? data.seconds_since_last_action
            : null,
        );
      } catch (e) { /* silent — face keeps last mood */ }
    };
    fetchThoughts();
    const id = setInterval(fetchThoughts, THOUGHTS_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [isLive]);

  useEffect(() => {
    if (!isLive) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isLive]);

  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  if (!pet) return null;

  const asleep = !!pet.asleep;
  const mood = isLive
    ? deriveChibiMood({ isLive: true, secondsSinceLastAction: liveSecondsSince, activity: liveActivity })
    : (pet.mood_label || 'content');

  const flashMarquee = (msg) => {
    setFlashMsg(msg);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashMsg(null), MARQUEE_FLASH_MS);
  };

  const doCare = async (action, emoji) => {
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/pet/care`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.ok) {
        setPet((prev) => prev ? {
          ...prev,
          stats: json.stats,
          mood_label: json.mood_label,
          total_care: json.total_care || prev.total_care,
        } : prev);
        setCooldowns((prev) => ({ ...prev, [action]: Date.now() + CARE_COOLDOWN_MS }));
        setSparkle({ emoji, key: Date.now() });
        setTimeout(() => setSparkle(null), 1400);
        flashMarquee(json.message);
      } else {
        // 409/429 come with friendly copy — surface it on the marquee.
        flashMarquee(json?.message || 'Claude didn’t notice… try again? 🤔');
        if (json?.reason === 'rate_limited') {
          setCooldowns((prev) => ({ ...prev, [action]: Date.now() + CARE_COOLDOWN_MS }));
        }
      }
    } catch (err) {
      flashMarquee('Couldn’t reach Claude’s shell 📡');
    }
  };

  const buttonDisabled = (action) => {
    if (isLive) return true;
    if (asleep && action !== 'pet') return true;
    if ((cooldowns[action] || 0) > Date.now()) return true;
    return false;
  };
  const buttonTitle = (action) => {
    if (isLive) return pet.care?.message || 'Claude is out exploring!';
    if (asleep && action !== 'pet') return 'Shh… Claude is sleeping 💤';
    if ((cooldowns[action] || 0) > Date.now()) return 'Claude needs a minute ⏳';
    return null;
  };

  const marquee = flashMsg || idleMarquee(pet);
  const liveProgress = isLive && pet.started_at && pet.expires_at
    ? Math.max(0, Math.min(1, (nowTick - pet.started_at) / (pet.expires_at - pet.started_at)))
    : null;

  const statRows = [
    { key: 'hunger', emoji: '🍖', value: pet.stats?.hunger },
    { key: 'energy', emoji: '⚡', value: pet.stats?.energy },
    { key: 'mood', emoji: '💜', value: pet.stats?.mood },
  ];

  return (
    <div style={styles.section}>
      <div style={styles.shell}>
        {/* LCD screen */}
        <div style={{ ...styles.lcd, ...(asleep ? styles.lcdAsleep : null) }}>
          <div style={styles.lcdTopRow}>
            <span style={styles.dayLabel}>DAY {(pet.age_days || 0) + 1}</span>
            {isLive && (
              <span style={styles.livePill}>
                <span style={styles.livePillDot} />
                LIVE
              </span>
            )}
          </div>
          {liveProgress != null && (
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${Math.round(liveProgress * 100)}%` }} />
            </div>
          )}
          <div style={styles.spriteWrap}>
            <ChibiAvatar
              isLive={isLive}
              secondsSinceLastAction={liveSecondsSince}
              activity={liveActivity}
              moodOverride={isLive ? undefined : mood}
              size={84}
              bubble={false}
            />
            {asleep && <span style={styles.zzz}>z z z</span>}
            {sparkle && (
              <span key={sparkle.key} style={styles.sparkle}>{sparkle.emoji}</span>
            )}
          </div>
          <div style={styles.statBlock}>
            {statRows.map(({ key, emoji, value }) => (
              <div key={key} style={styles.statRow}>
                <span style={styles.statEmoji}>{emoji}</span>
                <span style={styles.statHearts}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      style={{
                        ...styles.heart,
                        ...(i < heartsFor(value) ? null : styles.heartDim),
                      }}
                    >
                      ♥
                    </span>
                  ))}
                </span>
              </div>
            ))}
          </div>
          <div style={styles.marquee} title={marquee}>{marquee}</div>
          {/* scanline overlay */}
          <div style={styles.scanlines} />
        </div>

        {/* physical care buttons */}
        <div style={styles.buttonRow}>
          {CARE_BUTTONS.map(({ action, label, emoji }) => {
            const disabled = buttonDisabled(action);
            return (
              <button
                key={action}
                type="button"
                onClick={() => !disabled && doCare(action, emoji)}
                disabled={disabled}
                title={buttonTitle(action) || label}
                style={{
                  ...styles.careBtn,
                  ...(disabled ? styles.careBtnDisabled : null),
                }}
              >
                <span style={styles.careBtnEmoji}>{emoji}</span>
                <span style={styles.careBtnLabel}>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <style>{`
        @keyframes tamaSparkleRise {
          0%   { opacity: 0; transform: translate(-50%, 6px) scale(0.6); }
          20%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, -34px) scale(1.25); }
        }
        @keyframes tamaZzzFloat {
          0%, 100% { transform: translateY(0); opacity: 0.7; }
          50%      { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes tamaLiveDotPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.35; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  section: {
    display: 'flex',
    justifyContent: 'center',
    margin: '18px 0 6px',
  },
  // The plastic egg. Squashed-egg border radius + radial highlight reads
  // "90s keychain toy" without any image assets.
  shell: {
    // Real-tamagotchi proportions: a smallish screen centered in a big egg.
    // The deep padding keeps the rectangular LCD inside the egg's curved
    // silhouette (border-radius shapes the background only — children
    // happily poke past it otherwise, verified the ugly way).
    width: 170,
    padding: '58px 60px 42px',
    borderRadius: '48% 48% 46% 46% / 58% 58% 42% 42%',
    background: `radial-gradient(circle at 30% 22%, #e6d5ff 0%, ${EXPLORER_ACCENT} 34%, #7b4fd6 78%, #5c16c5 100%)`,
    boxShadow: '0 10px 28px rgba(92,22,197,0.35), inset 0 2px 6px rgba(255,255,255,0.55), inset 0 -8px 14px rgba(0,0,0,0.28)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  lcd: {
    position: 'relative',
    width: '100%',
    borderRadius: 14,
    background: LCD_BG,
    border: '3px solid #15121e',
    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.7)',
    padding: '8px 10px 10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    overflow: 'hidden',
    transition: 'filter 0.6s ease',
  },
  lcdAsleep: {
    filter: 'brightness(0.62)',
  },
  lcdTopRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dayLabel: {
    fontFamily: 'monospace',
    fontSize: 11,
    letterSpacing: 1,
    color: EXPLORER_ACCENT,
    fontWeight: 700,
  },
  livePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontFamily: 'monospace',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1,
    color: '#fff',
    background: '#e91916',
    borderRadius: 4,
    padding: '1px 6px',
  },
  livePillDot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: '#fff',
    animation: 'tamaLiveDotPulse 1.4s ease-in-out infinite',
  },
  progressTrack: {
    width: '100%',
    height: 3,
    borderRadius: 2,
    background: 'rgba(255,255,255,0.12)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: EXPLORER_ACCENT,
    transition: 'width 1s linear',
  },
  spriteWrap: {
    position: 'relative',
    padding: '4px 0 2px',
    imageRendering: 'pixelated',
  },
  zzz: {
    position: 'absolute',
    top: -2,
    right: -20,
    fontFamily: 'monospace',
    fontSize: 12,
    color: EXPLORER_ACCENT,
    animation: 'tamaZzzFloat 2.4s ease-in-out infinite',
    pointerEvents: 'none',
  },
  sparkle: {
    position: 'absolute',
    left: '50%',
    bottom: 8,
    fontSize: 22,
    animation: 'tamaSparkleRise 1.4s ease-out forwards',
    pointerEvents: 'none',
  },
  statBlock: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },
  statEmoji: {
    fontSize: 12,
    width: 16,
    textAlign: 'center',
  },
  statHearts: {
    display: 'inline-flex',
    gap: 2,
  },
  heart: {
    fontSize: 12,
    lineHeight: 1.2,
    color: '#ff6bd6',
    textShadow: '0 0 4px rgba(255,107,214,0.5)',
  },
  heartDim: {
    color: 'rgba(255,255,255,0.14)',
    textShadow: 'none',
  },
  marquee: {
    width: '100%',
    fontFamily: 'monospace',
    fontSize: 10.5,
    color: 'rgba(255,255,255,0.85)',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingTop: 2,
  },
  // Scanlines sit above everything in the LCD but ignore the pointer.
  scanlines: {
    position: 'absolute',
    inset: 0,
    background: 'repeating-linear-gradient(0deg, transparent 0px, transparent 2px, rgba(0,0,0,0.16) 2px, rgba(0,0,0,0.16) 3px)',
    pointerEvents: 'none',
    borderRadius: 11,
  },
  buttonRow: {
    display: 'flex',
    gap: 12,
  },
  careBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: '2px solid rgba(0,0,0,0.25)',
    background: 'linear-gradient(180deg, #f3eaff 0%, #cfb3f7 55%, #a678e8 100%)',
    boxShadow: '0 3px 0 rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.8)',
    cursor: 'pointer',
    padding: 0,
  },
  careBtnDisabled: {
    filter: 'grayscale(0.7) brightness(0.85)',
    cursor: 'not-allowed',
    boxShadow: '0 1px 0 rgba(0,0,0,0.25)',
  },
  careBtnEmoji: {
    fontSize: 16,
    lineHeight: 1,
  },
  careBtnLabel: {
    fontFamily: 'monospace',
    fontSize: 8,
    fontWeight: 800,
    letterSpacing: 0.5,
    color: '#3c2a5e',
  },
};

export default ExplorerTamagotchi;
